/**
 * CurrencySelector Component
 *
 * Allows users to select a currency/token for the airdrop.
 *
 * @component
 */

import React, { useState, useMemo } from "react";
import { colors, commonStyles } from "../styles";

const CurrencySelector = ({
  currencies,
  value,
  onChange,
  disabled,
  loading,
}) => {
  const [search, setSearch] = useState("");

  const filteredCurrencies = useMemo(() => {
    // First deduplicate based on symbol
    const uniqueMap = new Map();
    currencies.forEach((c) => {
      const symbol = c.symbol || c;
      if (!uniqueMap.has(symbol)) {
        uniqueMap.set(symbol, c);
      }
    });

    const uniqueCurrencies = Array.from(uniqueMap.values());

    if (!search) return uniqueCurrencies;

    return uniqueCurrencies.filter((c) => {
      const symbol = c.symbol || c;
      const name = c.name || "";
      return (
        symbol.toLowerCase().includes(search.toLowerCase()) ||
        name.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [currencies, search]);

  return (
    <div
      className="currency-selector panel"
      style={{
        ...commonStyles.panel,
        marginBottom: "20px",
      }}
    >
      <h3 style={commonStyles.panelHeader}>Select Currency</h3>

      {/* Search and Selection Info */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 20px",
          background: colors.gray100,
          borderBottom: `1px solid ${colors.border}`,
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ flex: 1, minWidth: "200px", maxWidth: "350px" }}>
          <input
            type="text"
            placeholder="Search currency by symbol or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
            style={commonStyles.input}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            style={{
              padding: "6px 14px",
              background: value ? colors.accent : "#6c757d",
              color: colors.white,
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {value ? "1 selected" : "0 selected"}
          </span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="wallet-table-loading">
          <div className="spinner"></div>
          <span>Loading currencies...</span>
        </div>
      ) : filteredCurrencies.length === 0 ? (
        <div className="wallet-table-empty">
          <p>No currencies found matching your search</p>
        </div>
      ) : (
        <div
          className="index_table_container"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          <table className="index_table currency-table">
            <thead>
              <tr>
                <th className="col col-selectable"></th>
                <th className="col">Symbol</th>
                <th className="col">Name</th>
                <th className="col">Token ID</th>
                <th className="col col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCurrencies.map((c, index) => {
                const symbol = c.symbol || c;
                const name = c.name || "-";
                const tokenId = c.tokenId || "-";
                const isSelected = value === symbol;

                return (
                  <tr
                    key={symbol}
                    className={`${isSelected ? "selected" : ""} ${
                      index % 2 === 0 ? "even" : "odd"
                    }`}
                    onClick={() => !disabled && onChange(symbol)}
                  >
                    <td className="col col-selectable">
                      <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => !disabled && onChange(symbol)}
                        disabled={disabled}
                      />
                    </td>
                    <td className="col">
                      <strong>{symbol}</strong>
                    </td>
                    <td className="col">{name}</td>
                    <td className="col">
                      <code>{tokenId}</code>
                    </td>
                    <td className="col col-status">
                      <span className="status_tag active">Active</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
